import { useState } from 'react';
import { Tab } from '@headlessui/react';
import {
  VideoCameraIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';

interface LocationDetails {
  name: string;
  address: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
}

export default function WarRoom() {
  const [warRoomDetails, setWarRoomDetails] = useState({
    name: 'Crisis Response War Room',
    videoLink: 'https://meet.example.com/crisis-room',
    callInNumber: '+1 (555) 000-0000',
    callInPin: '123456',
    webLink: 'https://crisis.example.com/war-room',
  });

  const [physicalLocations, setPhysicalLocations] = useState({
    primary: {
      name: 'Main War Room',
      address: '123 Corporate HQ, 10th Floor',
      contactName: 'John Smith',
      contactPhone: '+1 (555) 123-4567',
      contactEmail: 'john.smith@company.com',
    },
    backup: {
      name: 'Backup War Room',
      address: '456 Branch Office, 5th Floor',
      contactName: 'Jane Doe',
      contactPhone: '+1 (555) 234-5678',
      contactEmail: 'jane.doe@company.com',
    },
    disaster: {
      name: 'Disaster Recovery Site',
      address: '789 Remote Location',
      contactName: 'Mike Johnson',
      contactPhone: '+1 (555) 345-6789',
      contactEmail: 'mike.johnson@company.com',
    },
  });

  const handleInputChange = (field: string, value: string) => {
    setWarRoomDetails(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLocationChange = (
    locationType: 'primary' | 'backup' | 'disaster',
    field: keyof LocationDetails,
    value: string
  ) => {
    setPhysicalLocations(prev => ({
      ...prev,
      [locationType]: {
        ...prev[locationType],
        [field]: value
      }
    }));
  };

  const LocationForm = ({ 
    type, 
    title, 
    data 
  }: { 
    type: 'primary' | 'backup' | 'disaster';
    title: string;
    data: LocationDetails;
  }) => (
    <div className="border rounded-lg p-4">
      <h4 className="font-medium text-lg mb-4">{title}</h4>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Room Name</label>
          <input
            type="text"
            value={data.name}
            onChange={(e) => handleLocationChange(type, 'name', e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Address</label>
          <input
            type="text"
            value={data.address}
            onChange={(e) => handleLocationChange(type, 'address', e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Contact Person</label>
          <input
            type="text"
            value={data.contactName}
            onChange={(e) => handleLocationChange(type, 'contactName', e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Contact Phone</label>
            <input
              type="tel"
              value={data.contactPhone}
              onChange={(e) => handleLocationChange(type, 'contactPhone', e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Contact Email</label>
            <input
              type="email"
              value={data.contactEmail}
              onChange={(e) => handleLocationChange(type, 'contactEmail', e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const [categories] = useState([
    {
      id: 'overview',
      name: 'Overview',
      icon: UserGroupIcon,
      content: (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-800">
              The war room acts as the command center during the cyber crisis where the crisis response team and stakeholders execute the crisis response plan and activities. It could be a physical and/or virtual meeting space and even physically distributed for redundancy purposes.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="border rounded-lg p-4">
              <h4 className="font-medium text-lg mb-2">Physical War Room</h4>
              <ul className="space-y-2 text-gray-600">
                <li>• Main Conference Room - 10th Floor</li>
                <li>• Backup Location - 5th Floor</li>
                <li>• Disaster Recovery Site - Remote Location</li>
              </ul>
            </div>
            <div className="border rounded-lg p-4">
              <h4 className="font-medium text-lg mb-2">Virtual War Room</h4>
              <ul className="space-y-2 text-gray-600">
                <li>• Primary Video Conference Link</li>
                <li>• Backup Communication Channels</li>
                <li>• Secure Document Sharing</li>
              </ul>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'physical-locations',
      name: 'Physical Locations',
      icon: BuildingOfficeIcon,
      content: (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="space-y-6">
            <LocationForm 
              type="primary" 
              title="Primary War Room" 
              data={physicalLocations.primary} 
            />
            <LocationForm 
              type="backup" 
              title="Backup War Room" 
              data={physicalLocations.backup} 
            />
            <LocationForm 
              type="disaster" 
              title="Disaster Recovery Site" 
              data={physicalLocations.disaster} 
            />
            <div className="flex justify-end">
              <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                Save All Locations
              </button>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'virtual-room',
      name: 'Virtual Room',
      icon: VideoCameraIcon,
      content: (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="space-y-6">
            <div className="border-b pb-6">
              <h3 className="text-lg font-medium mb-4">Virtual War Room Configuration</h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="warRoomName" className="block text-sm font-medium text-gray-700">
                    War Room Name
                  </label>
                  <input
                    type="text"
                    id="warRoomName"
                    value={warRoomDetails.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="videoLink" className="block text-sm font-medium text-gray-700">
                    Video Conference Link
                  </label>
                  <input
                    type="text"
                    id="videoLink"
                    value={warRoomDetails.videoLink}
                    onChange={(e) => handleInputChange('videoLink', e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="callInNumber" className="block text-sm font-medium text-gray-700">
                      Call-in Number
                    </label>
                    <input
                      type="text"
                      id="callInNumber"
                      value={warRoomDetails.callInNumber}
                      onChange={(e) => handleInputChange('callInNumber', e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="callInPin" className="block text-sm font-medium text-gray-700">
                      Call-in PIN
                    </label>
                    <input
                      type="text"
                      id="callInPin"
                      value={warRoomDetails.callInPin}
                      onChange={(e) => handleInputChange('callInPin', e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="webLink" className="block text-sm font-medium text-gray-700">
                    Web Access Link
                  </label>
                  <input
                    type="text"
                    id="webLink"
                    value={warRoomDetails.webLink}
                    onChange={(e) => handleInputChange('webLink', e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>
                <div className="mt-4 flex space-x-3">
                  <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                    Save Configuration
                  </button>
                  <button className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                    Join Virtual Room
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'resources',
      name: 'Resources',
      icon: DocumentTextIcon,
      content: (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border rounded-lg p-4">
                <h4 className="font-medium">Emergency Contacts</h4>
                <ul className="mt-2 space-y-2 text-sm text-gray-600">
                  <li>Crisis Manager: +1 (555) 123-4567</li>
                  <li>Technical Lead: +1 (555) 234-5678</li>
                  <li>Communications: +1 (555) 345-6789</li>
                </ul>
              </div>
              <div className="border rounded-lg p-4">
                <h4 className="font-medium">Quick Access Documents</h4>
                <ul className="mt-2 space-y-2 text-sm text-gray-600">
                  <li>Response Playbook</li>
                  <li>Communication Templates</li>
                  <li>Escalation Procedures</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'status-updates',
      name: 'Status Updates',
      icon: ChatBubbleLeftRightIcon,
      content: (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-4">
              <div className="border rounded-lg p-4">
                <h4 className="font-medium">Status Updates</h4>
                <div className="mt-4 space-y-4">
                  <div className="bg-yellow-50 p-3 rounded-md">
                    <span className="text-sm font-medium text-yellow-800">Incident #2024-001</span>
                    <p className="text-sm text-yellow-600 mt-1">Investigation in progress</p>
                  </div>
                  <div className="bg-green-50 p-3 rounded-md">
                    <span className="text-sm font-medium text-green-800">Incident #2024-002</span>
                    <p className="text-sm text-green-600 mt-1">Containment phase completed</p>
                  </div>
                </div>
              </div>
              <div className="border rounded-lg p-4">
                <h4 className="font-medium">Team Chat</h4>
                <div className="mt-4">
                  <button className="w-full px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                    Open Secure Chat
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
  ]);

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">War Room</h2>
      <Tab.Group>
        <Tab.List className="flex space-x-4 border-b border-gray-200">
          {categories.map((category) => (
            <Tab
              key={category.id}
              className={({ selected }) =>
                clsx(
                  'flex items-center space-x-2 py-2 px-4 text-sm font-medium border-b-2 outline-none',
                  selected
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                )
              }
            >
              <category.icon className="h-5 w-5" />
              <span>{category.name}</span>
            </Tab>
          ))}
        </Tab.List>
        <Tab.Panels>
          {categories.map((category) => (
            <Tab.Panel key={category.id}>{category.content}</Tab.Panel>
          ))}
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
}